import { AD_TYPES, PLACEMENTS, DEVICES } from "@/lib/adminApi";
import { categories } from "@/lib/toolsData";
import { AdRenderer } from "@/components/AdRenderer";

const Label = ({ children }) => <label className="mb-1 block text-xs font-semibold text-neutral-600">{children}</label>;
const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500";

const Text = ({ k, ad, set, label, ph, type = "text" }) => (
  <div><Label>{label}</Label>
    <input data-testid={`adform-${k}`} type={type} className={inputCls} value={ad[k] ?? ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} /></div>
);

const Area = ({ k, ad, set, label, ph }) => (
  <div><Label>{label}</Label>
    <textarea data-testid={`adform-${k}`} rows={4} className={`${inputCls} font-mono text-xs`} value={ad[k] ?? ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} /></div>
);

const Check = ({ k, ad, set, label }) => (
  <label className="flex items-center gap-2 text-sm"><input data-testid={`adform-${k}`} type="checkbox" checked={!!ad[k]} onChange={(e) => set(k, e.target.checked)} /> {label}</label>
);

const CSV = ({ k, ad, set, label, ph }) => (
  <div><Label>{label}</Label>
    <input data-testid={`adform-${k}`} className={inputCls} value={(ad[k] || []).join(", ")} placeholder={ph}
      onChange={(e) => set(k, e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></div>
);

const toggleArr = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export const AdForm = ({ ad, setAd }) => {
  const set = (k, v) => setAd({ ...ad, [k]: v });
  const t = ad.type;
  const isImg = ["image-banner", "affiliate-banner", "sticky-footer", "sticky-sidebar", "floating", "popup"].includes(t);
  const isAffiliate = ["affiliate-button", "affiliate-card", "native"].includes(t);
  const isHtml = ["html-banner", "inline-content", "ad-manager"].includes(t);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <Text k="name" ad={ad} set={set} label="Ad name (internal)" ph="Summer Canva promo" />
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label>
            <select data-testid="adform-type" className={inputCls} value={t} onChange={(e) => set("type", e.target.value)}>
              {AD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div><Label>Status</Label>
            <select data-testid="adform-status" className={inputCls} value={ad.status} onChange={(e) => set("status", e.target.value)}>
              <option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Text k="priority" ad={ad} set={set} label="Priority (higher = first)" type="number" />
          <Text k="frequency" ad={ad} set={set} label="Frequency cap (0 = none)" type="number" />
        </div>
        <Check k="enabled" ad={ad} set={set} label="Enabled (live immediately)" />

        {isImg && (<>
          <Text k="image_url" ad={ad} set={set} label="Image URL" ph="https://…/banner.png" />
          <Text k="link_url" ad={ad} set={set} label="Destination / affiliate URL" ph="https://…" />
          <Text k="cta_text" ad={ad} set={set} label="CTA text (optional)" ph="Shop now" />
          <Check k="new_tab" ad={ad} set={set} label="Open link in new tab" />
        </>)}

        {isAffiliate && (<>
          <Text k="image_url" ad={ad} set={set} label="Image URL" />
          <Area k="html" ad={ad} set={set} label="Short description" />
          <Text k="link_url" ad={ad} set={set} label="Affiliate URL" />
          <Text k="cta_text" ad={ad} set={set} label="Button text" ph="View deal" />
          <Check k="new_tab" ad={ad} set={set} label="Open link in new tab" />
        </>)}

        {isHtml && <Area k="html" ad={ad} set={set} label="HTML / JavaScript code" ph="<div>…</div>" />}

        {t === "adsense" && (<>
          <Text k="adsense_client" ad={ad} set={set} label="AdSense client (ca-pub-…)" />
          <Text k="adsense_slot" ad={ad} set={set} label="Ad slot ID" />
          <Text k="adsense_format" ad={ad} set={set} label="Format" ph="auto" />
          <Area k="script" ad={ad} set={set} label="Or paste full AdSense code (overrides above)" />
        </>)}

        {t === "video" && (<>
          <div><Label>Video source</Label>
            <select className={inputCls} value={ad.video_type} onChange={(e) => set("video_type", e.target.value)}>
              <option value="youtube">YouTube</option><option value="vimeo">Vimeo</option><option value="mp4">Self-hosted MP4</option>
            </select></div>
          <Text k="video_url" ad={ad} set={set} label="Video URL" ph="https://youtube.com/watch?v=…" />
          <div className="flex gap-4"><Check k="autoplay" ad={ad} set={set} label="Autoplay" /><Check k="muted" ad={ad} set={set} label="Muted" /><Check k="loop" ad={ad} set={set} label="Loop" /></div>
          <Text k="skip_after" ad={ad} set={set} label="Skip after (seconds, 0 = none)" type="number" />
        </>)}

        {t === "announcement-bar" && (<>
          <Text k="html" ad={ad} set={set} label="Message" ph="🎉 New: 10 AI tools added" />
          <Text k="link_url" ad={ad} set={set} label="Button URL (optional)" />
          <Text k="cta_text" ad={ad} set={set} label="Button label" ph="See what's new" />
        </>)}
      </div>

      <div className="space-y-4">
        <div>
          <Label>Placements</Label>
          <div className="grid max-h-40 grid-cols-1 gap-1 overflow-auto rounded-lg border border-neutral-200 p-2 text-sm">
            {PLACEMENTS.map(([v, l]) => (
              <label key={v} className="flex items-center gap-2">
                <input data-testid={`adform-placement-${v}`} type="checkbox" checked={ad.placements.includes(v)} onChange={() => set("placements", toggleArr(ad.placements, v))} /> {l}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Device targeting (none = all)</Label>
          <div className="flex gap-4 text-sm">
            {DEVICES.map((d) => (
              <label key={d} className="flex items-center gap-1.5 capitalize">
                <input type="checkbox" checked={ad.devices.includes(d)} onChange={() => set("devices", toggleArr(ad.devices, d))} /> {d}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Category targeting (none = all)</Label>
          <div className="grid grid-cols-2 gap-1 text-sm">
            {categories.map((c) => (
              <label key={c.id} className="flex items-center gap-1.5">
                <input type="checkbox" checked={ad.categories.includes(c.id)} onChange={() => set("categories", toggleArr(ad.categories, c.id))} /> {c.name}
              </label>
            ))}
          </div>
        </div>

        <CSV k="tools" ad={ad} set={set} label="Specific tool slugs (comma sep, none = all)" ph="pdf-merge, bmi" />
        <CSV k="countries" ad={ad} set={set} label="Country codes (comma sep, none = all)" ph="US, IN, GB" />

        <div className="grid grid-cols-2 gap-3">
          <div><Label>Start date</Label><input data-testid="adform-start" type="datetime-local" className={inputCls} value={(ad.start_date || "").slice(0, 16)} onChange={(e) => set("start_date", e.target.value ? e.target.value + ":00" : null)} /></div>
          <div><Label>End date</Label><input data-testid="adform-end" type="datetime-local" className={inputCls} value={(ad.end_date || "").slice(0, 16)} onChange={(e) => set("end_date", e.target.value ? e.target.value + ":00" : null)} /></div>
        </div>

        <div>
          <Label>Live preview</Label>
          <div className="flex min-h-[80px] items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 p-3">
            <AdRenderer ad={{ ...ad, id: "preview" }} preview />
          </div>
        </div>
      </div>
    </div>
  );
};
